
import WDElement from '../wd-element.js';
import { WDListItem, WDListHeader, WDListFooter } from './wd-list-item';

export default class WDList extends WDElement
{

	#wrapper = null;
	#body = null;
	#header = null;
	#footer = null;
	#valueField = 'value';	
	#focusable = false;
	#selectedIndex = 0;

	constructor() 
	{
		super().classList.add('wd-list');

		//build the base structure
		this.#wrapper = wdc('<div class="wd-list-body-wrapper"/>');
		this.#body = wdc('<div class="wd-list-body"/>');

		//assemble the wrapper and body - required
		this.append(this.#wrapper);
		this.#wrapper.append(this.#body);

		return this;
	};

	//public accessors	
	get body() { return this.#body; };
	get wrapper() { return this.#wrapper; };
	get items() { return this.#body.children; };
	get itemArray() { return [...this.#body.children]; };

	get valueField() { return this.#valueField; };
	set valueField(vf) { this.#valueField = vf; };

	set responsive(mode)
	{
		if (mode === false) this.classList.remove('responsive');
		else this.classList.add('responsive');
	};

	//
	get header()
	{
		if (!this.#header)
		{
			this.#header = new WDListHeader(this);
			this.#wrapper.before(this.#header);
		}

		return this.#header;
	};

	get footer()
	{
		if (!this.#footer)
		{
			this.#footer = new WDListFooter(this);
			this.#wrapper.after(this.#footer);
		}

		return this.#footer;
	};

	createItem()
	{
		const item = new WDListItem(this);
		item.on('item:selected', () => {
			this.deselectAll();
			this.trigger('item:selected',item);
		});

		return item;
	};

	add()
	{
		return this.insert(null);
	};

	single()
	{
		this.reset();
		return this.insert(0);
	};

	insert(idx)
	{
		const item = this.createItem();

		if (idx === 0) 
		{
			this.#body.prepend(item);
		}
		else if (idx !== null && idx < this.items.length)
		{
			this.items[idx].before(item);
		}
		else
		{
			this.#body.append(item);
		}

		return item;
	};

	replaceItemAt(idx)
	{
		const item = this.createItem();

		if (idx < this.items.length)
		{
			const r = this.items[idx];

			r.before(item);
			r.remove();
		}
		else
		{
			this.#body.append(item);
		}

		return item;
	};

	removeItemAt(idx)
	{
		if (typeof (this.items[idx]) != 'undefined')
		{
			this.items[idx].remove();
		}
	};

	removeItems(arr)
	{
		arr.forEach(item => item.remove());
	};

	get selected()
	{
		return this.itemArray.find( x => x.selected == true );
	};

	/** selects a item based on the passed value **/
	selectFromVal(val)
	{
		const item = this.itemFromVal(val);
		if (item) 
		{
			item.selected = true;
			item.scrollTo();
		}
	};

	deselectAll()
	{
		const selItem = this.selected;
		if (selItem) selItem.selected = false;
	};

	/** selects a item based on the passed value **/
	itemFromVal(val)
	{
		return this.itemArray.find( x => x.value == val );
	};

	reset()
	{
		this.#body.replaceChildren();
	};

	get checked()
	{
		return this.itemArray.filter( x => x.checked == true);
	};

	get value()
	{
		return this.checked.map( x => x.value );
	};

	set value(vals)
	{
		this.itemArray().forEach(item => {
			item.checked = vals.includes(item.value);
		});
	}

	get data() {
		return this.checked.map( x => x.data );
	};

	set data(vals)
	{
		const setVals = vals.map(x => x[this.valueField()]);
		this.itemArray.forEach(item => {
			item.checked = setVals.includes( item.value );
		});
	}

	get allData()
	{
		return this.itemArray.map( x => x.data );
	};

	get focusable() { return this.#focusable; };
	set focusable(val)
	{
		this.#focusable = val;
		if (val === true) this.addFocusEvents();	
	};

	resetSelectedIndex()
	{
		this.#selectedIndex = '0';
	};

	focusFirstItem()
	{
		this.#selectedIndex = '0';
		this.items[0].focus();
	}

	addFocusEvents()
	{
		this.tabIndex = '0';
		this.#selectedIndex = '0';

		//focus on the first item when the list is focused
		this.on('item:selected', (evt) =>
		{
			this.#selectedIndex = evt.detail.index;
		})

		this.on('results:fetched', () => this.#selectedIndex = '0');

		/*
		//this causes problems for when the list is focused in by some other method
		so we just have other items call focusFirstItem() instead
		this.on('focusin',(evt) => {
			
			if (evt.relatedTarget.classList.contains('form-control'))
			{
				this.items[this.#selectedIndex].focus();
			}
			
		})
		*/
		this.on('focusout',(evt) => {
			
			// Only reset if focus is moving completely outside the list
			// relatedTarget is the element receiving focus
			if (!evt.relatedTarget || !this.contains(evt.relatedTarget)) {
				this.#selectedIndex = 0;
			}
		})

		// Keyboard navigation
		this.addEventListener('keydown', (e) =>
		{
			if (e.key === 'ArrowDown')
			{
				// Stop at the end instead of wrapping
				if (this.#selectedIndex < this.items.length - 1) {
					this.#selectedIndex = +this.#selectedIndex + 1;
					this.items[this.#selectedIndex].focus();
				}
				else
				{
					this.trigger('list:nav-escape', { direction: 'down' });
				}

				e.preventDefault();
			} 
			else if (e.key === 'ArrowUp')
			{
				// Stop at the beginning instead of wrapping
				if (this.#selectedIndex > 0) {
					this.#selectedIndex = this.#selectedIndex - 1;
					this.items[this.#selectedIndex].focus();
				}
				else
				{
					this.trigger('list:nav-escape', {direction:'up'});
				}
				e.preventDefault();
			}
			else if (e.key === 'Tab')
			{
				if (e.shiftKey)
				{
					this.trigger('list:nav-escape', {direction:'up'});
				}
				else
				{
					this.trigger('list:nav-escape', {direction:'down'});
				}
				
				e.preventDefault();
			}

		});

	};

};

customElements.define('wd-list', WDList);
